import { useState, useRef, useEffect } from 'react';

function formatTime(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m   = Math.floor(s / 60);
  const sec = String(Math.floor(s % 60)).padStart(2, '0');
  return `${m}:${sec}`;
}

export default function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);

  // Pause audio when the component unmounts (navigation away)
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) audio.pause();
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect  = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = Math.max(0, Math.min(ratio * duration, duration));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      style={{
        background:    '#eef1f7',
        borderRadius:  '12px',
        padding:       '20px',
      }}
    >
      {/* Section label */}
      <p
        style={{
          margin:        '0 0 14px',
          fontSize:      '0.75rem',
          fontWeight:    700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color:         '#374151',
        }}
      >
        🎵 Play Audio
      </p>

      {/* Hidden native audio element */}
      <audio
        ref={audioRef}
        src={src}
        style={{ display: 'none' }}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Gold play / pause circle */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          style={{
            width:           '56px',
            height:          '56px',
            borderRadius:    '50%',
            background:      '#c9a84c',
            border:          'none',
            cursor:          'pointer',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            fontSize:        '20px',
            color:           '#fff',
            flexShrink:      0,
            transition:      'background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#b8962e'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#c9a84c'; }}
        >
          {playing ? '⏸' : '▶'}
        </button>

        {/* Progress bar + elapsed / total */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            role="progressbar"
            aria-valuenow={Math.round(currentTime)}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration) || 1}
            onClick={handleProgressClick}
            style={{
              height:       '6px',
              background:   '#d1d5db',
              borderRadius: '3px',
              cursor:       'pointer',
              position:     'relative',
            }}
          >
            <div
              style={{
                height:       '100%',
                width:        `${progress}%`,
                background:   '#0f1e3d',
                borderRadius: '3px',
                transition:   'width 0.1s linear',
              }}
            />
          </div>
          <div
            style={{
              display:        'flex',
              justifyContent: 'flex-end',
              marginTop:      '4px',
              fontSize:       '0.72rem',
              color:          '#6b7280',
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      </div>
    </div>
  );
}
